import { appRoutes } from 'constants/navigation';
import { useCompanyPageCompanyQuery } from 'generated/graphql';
import { useSerializedQueryParams } from 'hooks/useQueryParams';
import { ICompanyPath } from 'interfaces/routes';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TABS_URL_VALUES,
  tabsArray,
  tabsIndexMap,
  UrlStateType,
} from 'views/Company/CompanyView/CompanyView.helpers';
import { AuditLogTab } from 'views/Company/CompanyView/components/AuditLogTab/AuditLogTab';
import BaseCompanyDetails from 'views/Company/CompanyView/components/BaseCompanyDetails/BaseCompanyDetails';
import CompanyEmailDomains from 'views/Company/CompanyView/components/CompanyEmailDomains/CompanyEmailDomains';
import { FeaturesTab } from 'views/Company/CompanyView/components/FeaturesTab/FeaturesTab';
import { IntegrationsTab } from 'views/Company/CompanyView/components/IntegrationsTab/IntegrationsTab';
import { KomboIntegrationWarningBanner } from 'views/Company/CompanyView/components/KomboIntegrationWarningBanner/KomboIntegrationWarningBanner';
import Layout from 'views/Company/CompanyView/components/Layout/Layout';
import MfaTab from 'views/Company/CompanyView/components/MfaTab/MfaTab';
import UsersTab from 'views/Company/CompanyView/components/UsersTab/UsersTab';
import {
  InlineNotification,
  NotificationType,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@spotted-zebra-uk/ui-components';
import styles from './CompanyView.module.scss';

export default function CompanyView() {
  const { companyId } = useParams() as unknown as ICompanyPath;
  const navigate = useNavigate();
  const parsedCompanyId = parseInt(companyId);
  const companyQueryResponse = useCompanyPageCompanyQuery({
    variables: {
      id: parsedCompanyId,
    },
    skip: !companyId,
  });
  const company = companyQueryResponse.data?.company;
  const [urlState, setUrlState] = useSerializedQueryParams<UrlStateType>({
    activeTab: TABS_URL_VALUES.Features,
  });

  const handleTabSelection = (tabIndex: number) => {
    const tab = tabsArray[tabIndex];
    setUrlState({ activeTab: tab });
  };

  useEffect(() => {
    if (!companyId) navigate(appRoutes.companies.path);
  }, [companyId, navigate]);

  if (companyQueryResponse.loading) {
    return <Layout loading />;
  }

  if (companyQueryResponse.error || !company) {
    return (
      <Layout>
        <InlineNotification notificationType={NotificationType.ERROR}>
          <p>Error loading company</p>
          <p>{companyQueryResponse.error?.message}</p>
        </InlineNotification>
      </Layout>
    );
  }

  return (
    <Layout>
      <BaseCompanyDetails company={company} />
      <KomboIntegrationWarningBanner
        atsIdentifier={company.atsIdentifier}
        companyId={parsedCompanyId}
      />
      <Tabs
        onSelectTab={handleTabSelection}
        selectedTabIndex={tabsIndexMap[urlState.activeTab]}
        className={styles.tabs}
      >
        <TabList>
          <Tab>Features</Tab>
          <Tab>Users</Tab>
          <Tab>Email Domains</Tab>
          <Tab>MFA</Tab>
          <Tab>Integrations</Tab>
          <Tab>Audit log</Tab>
        </TabList>

        <TabPanels>
          <TabPanel className={styles.tabPanel}>
            <FeaturesTab company={company} />
          </TabPanel>
          <TabPanel>
            <UsersTab companyId={parsedCompanyId} />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <CompanyEmailDomains
              allowedEmailDomains={company.allowedEmailDomains || []}
              companyId={parsedCompanyId}
            />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <MfaTab company={company} />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <IntegrationsTab company={company} />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <AuditLogTab companyId={parsedCompanyId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Layout>
  );
}
